import {
  Cell,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from "recharts"
import { gameRoles } from "@/utils/gameRoles"
import {
  ChartTooltip,
  EmptyHint,
  Panel,
  StatBar,
  StatTile,
  clampScore,
  formatDecimal,
  formatMoney,
  formatNumber,
  formatPercent,
} from "./statsUi"

// recharts 3's PolarAngleAxis is declared with a return type React 18's JSX
// namespace rejects, unlike every other chart part in this file. It renders
// fine; only the declaration is wrong, so it is cast rather than replaced.
const AngleAxis = PolarAngleAxis as unknown as React.FC<any>

// The per-game averages each playstyle axis is scored against. A player who
// averages the cap (or better) reads as 100 on that axis, so the shape of the
// radar says "this is what you do", not "this is how long you have played".
const PLAYSTYLE_CAPS = {
  rentPerGame: 5000,
  housesPerGame: 12,
  tradesPerGame: 5,
  auctionsPerGame: 5,
  stockPerGame: 3000,
}

const OverviewTab: React.FC<{ stats: IUserLifetimeStatsResponse }> = ({
  stats,
}) => {
  const games = stats.gamesPlayed || 0
  const perGame = (total: number) => (games > 0 ? total / games : 0)

  const playstyle = [
    {
      axis: "Landlord",
      score: clampScore(
        (perGame(stats.totalRentCollected) / PLAYSTYLE_CAPS.rentPerGame) * 100
      ),
    },
    {
      axis: "Builder",
      score: clampScore(
        (perGame(stats.housesBuilt) / PLAYSTYLE_CAPS.housesPerGame) * 100
      ),
    },
    {
      axis: "Trader",
      score: clampScore(
        (perGame(stats.tradesAccepted) / PLAYSTYLE_CAPS.tradesPerGame) * 100
      ),
    },
    {
      axis: "Bidder",
      score: clampScore(
        (perGame(stats.auctionsWon) / PLAYSTYLE_CAPS.auctionsPerGame) * 100
      ),
    },
    {
      axis: "Investor",
      score: clampScore(
        (perGame(stats.stockInvested) / PLAYSTYLE_CAPS.stockPerGame) * 100
      ),
    },
    {
      axis: "Survivor",
      score: clampScore(100 - stats.derived.bankruptcyRate),
    },
  ]

  const resultsSplit = [
    { name: "Wins", value: stats.gamesWon || 0, color: "#FBBF24" },
    {
      name: "Losses",
      value: Math.max((stats.gamesPlayed || 0) - (stats.gamesWon || 0), 0),
      color: "#475569",
    },
  ]

  const roleRows = Object.keys(stats.roleStats || {})
    .map((roleId) => {
      const entry = stats.roleStats[roleId as GameRoleId]
      const meta = gameRoles.find((role) => role.id === roleId)
      return {
        roleId,
        title: meta?.title || roleId,
        color: meta?.color || "#94A3B8",
        played: entry?.played || 0,
        won: entry?.won || 0,
      }
    })
    .filter((row) => row.played > 0)
    .sort((a, b) => b.played - a.played)

  const mostPlayedRole = roleRows[0]
  const bestRole = [...roleRows]
    .filter((row) => row.played >= 2)
    .sort((a, b) => b.won / b.played - a.won / a.played)[0]

  return (
    <div className="flex flex-col gap-4 sm:gap-2.5">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-2">
        <StatTile
          label="Games Played"
          value={formatNumber(stats.gamesPlayed)}
          sub={`${formatDecimal(stats.derived.averageTurnsPerGame, 0)} turns avg`}
          icon="fa-dice"
          accent="#FBBF24"
        />
        <StatTile
          label="Games Won"
          value={formatNumber(stats.gamesWon)}
          sub={`${formatPercent(stats.derived.winRate)} win rate`}
          icon="fa-trophy"
          accent="#34D399"
        />
        <StatTile
          label="Top Half"
          value={formatPercent(stats.derived.podiumRate, 0)}
          sub={`${formatNumber(stats.podiums)} finishes`}
          icon="fa-medal"
          accent="#38BDF8"
        />
        <StatTile
          label="Best Net Worth"
          value={formatMoney(stats.bestNetworth)}
          sub={`${formatMoney(stats.derived.averageNetworth)} avg`}
          icon="fa-sack-dollar"
          accent="#A78BFA"
        />
        <StatTile
          label="Bankruptcies"
          value={formatNumber(stats.timesBankrupted)}
          sub={`${formatPercent(stats.derived.bankruptcyRate, 0)} of games`}
          icon="fa-skull"
          accent="#F87171"
        />
        <StatTile
          label="Rivals Ruined"
          value={formatNumber(stats.bankruptedOpponents)}
          sub="opponents bankrupted"
          icon="fa-gavel"
          accent="#FB923C"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-2.5">
        <Panel
          title="Playstyle Profile"
          subtitle="How you win, scored against a strong game"
          icon="fa-chart-pie"
          accent="#FBBF24"
          className="lg:col-span-2"
        >
          {games === 0 ? (
            <EmptyHint text="Finish a game to build your profile." />
          ) : (
            <div className="h-72 sm:h-56 mobile:h-[150px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={playstyle} outerRadius="72%">
                  <PolarGrid stroke="rgba(255,255,255,0.12)" />
                  <AngleAxis
                    dataKey="axis"
                    tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 12 }}
                  />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="#FBBF24"
                    fill="#FBBF24"
                    fillOpacity={0.35}
                  />
                  <ChartTooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <Panel
          title="Win Record"
          subtitle="Every finished game"
          icon="fa-trophy"
          accent="#34D399"
        >
          {games === 0 ? (
            <EmptyHint text="No games recorded yet." />
          ) : (
            <>
              <div className="relative h-40 sm:h-32 mobile:h-[112px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={resultsSplit}
                      dataKey="value"
                      innerRadius="62%"
                      outerRadius="90%"
                      paddingAngle={2}
                      stroke="none"
                    >
                      {resultsSplit.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-fourth text-2xl mobile:text-[20px]/[1.15] font-bold text-amber-300">
                    {formatPercent(stats.derived.winRate, 0)}
                  </span>
                  <span className="text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
                    win rate
                  </span>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                <div className="rounded-xl border border-white/10 bg-white/5 py-2">
                  <p className="font-fourth text-lg mobile:text-[15px]/[1.25] font-bold text-amber-300">
                    {formatNumber(stats.winsByNetworth)}
                  </p>
                  <p className="text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
                    by net worth
                  </p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 py-2">
                  <p className="font-fourth text-lg mobile:text-[15px]/[1.25] font-bold text-rose-300">
                    {formatNumber(stats.winsByBankruptcy)}
                  </p>
                  <p className="text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
                    by knockout
                  </p>
                </div>
              </div>
            </>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-2.5">
        <Panel
          title="Role Mastery"
          subtitle="Games played and win rate per role"
          icon="fa-masks-theater"
          accent="#A78BFA"
        >
          {roleRows.length === 0 ? (
            <EmptyHint text="Play a game with a role to see mastery." />
          ) : (
            <div className="flex flex-col gap-3">
              {roleRows.map((row) => (
                <StatBar
                  key={row.roleId}
                  label={row.title}
                  value={`${row.won}/${row.played}`}
                  fraction={row.played > 0 ? row.won / row.played : 0}
                  color={row.color}
                  hint={`${formatPercent(
                    row.played > 0 ? (row.won / row.played) * 100 : 0,
                    0
                  )} win rate over ${row.played} game${row.played === 1 ? "" : "s"}`}
                />
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Form"
          subtitle="Streaks and standout runs"
          icon="fa-fire"
          accent="#FB923C"
        >
          <div className="grid grid-cols-2 gap-3 sm:gap-2">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
                Current streak
              </p>
              <p
                className={`mt-1 font-fourth text-xl mobile:text-[17px]/[1.25] font-bold ${
                  stats.currentWinStreak > 0
                    ? "text-emerald-300"
                    : stats.currentLossStreak > 0
                      ? "text-rose-300"
                      : "text-white/60"
                }`}
              >
                {stats.currentWinStreak > 0
                  ? `${stats.currentWinStreak}W`
                  : stats.currentLossStreak > 0
                    ? `${stats.currentLossStreak}L`
                    : "—"}
              </p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
                Best win streak
              </p>
              <p className="mt-1 font-fourth text-xl mobile:text-[17px]/[1.25] font-bold text-amber-300">
                {formatNumber(stats.bestWinStreak)}
              </p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
                Richest moment
              </p>
              <p className="mt-1 font-fourth text-xl mobile:text-[17px]/[1.25] font-bold text-emerald-300">
                {formatMoney(stats.richestSingleMoment)}
              </p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
                Peak rating
              </p>
              <p className="mt-1 font-fourth text-xl mobile:text-[17px]/[1.25] font-bold text-sky-300">
                {formatNumber(stats.peakRating)}
              </p>
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-3">
            <p className="text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
              Signature role
            </p>
            <p className="mt-1 font-fourth text-lg mobile:text-[15px]/[1.25] font-bold text-white">
              {mostPlayedRole ? mostPlayedRole.title : "—"}
            </p>
            <p className="mt-0.5 text-xs mobile:text-[11px]/[1.4] text-white/40">
              {bestRole
                ? `Best results as ${bestRole.title} — ${formatPercent(
                    (bestRole.won / bestRole.played) * 100,
                    0
                  )} win rate`
                : "Play at least two games with a role to rank it."}
            </p>
          </div>
        </Panel>
      </div>
    </div>
  )
}

export default OverviewTab
